import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import BlockIcon from '@mui/icons-material/Block';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Device } from '@twilio/voice-sdk';
import axios from 'axios';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import ClaimLink from '~/components/ClaimLink';
import mixpanel from '~/components/CmsMain/mixpanel';
import { channelTypeToIconSettings } from '~/components/communications/CommunicationCard/constants';
import { displayRoleName } from '~/components/communications/ContactUtils';
import { NewPhoneCallCommunicationCard } from '~/components/communications/PhoneCall/components/PhoneCallCommunicationCard/PhoneCallCommunicationCard';
import IncomingCallInProgressContainer from '~/components/communications/PhoneCall/IncomingCallInProgressContainer';
import OutgoingCallInProgressContainer from '~/components/communications/PhoneCall/OutgoingCallInProgressContainer';
import { attachTokenRefreshHandler, TOKEN_REFRESH_MS } from '~/components/communications/PhoneCall/twilioDeviceToken';
import { ContactEntity } from '~/components/Contact';
import { Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import Typography from '~/components/core/Atomic/Typography';
import { useCms } from '~/components/hooks/useCms';
import { PhoneIcon } from '~/components/icons/notifications';
import useOrganization from '~/components/OrganizationContext';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { reportAxiosError, reportErrorInProductionOrThrow, reportTwilioError } from '~/Utils';
import cn from '~/Utils/cn';

// The SDK raises a signaling 31204 / 31205 as AccessTokenInvalid (20101) / AccessTokenExpired (20104),
// and neither is ignored. AccessTokenInvalid is the only signal of a device whose first registration
// never completes. On AccessTokenExpired the SDK only stops refreshing the device's presence and leaves
// it registered, so the retry below may never run for it.
const TWILIO_ERRORS_TO_IGNORE = [31009, 31003, 31005, 31206, 31000];

const MAX_RE_REGISTER_ATTEMPTS = 3;
const RE_REGISTER_BACKOFF_MS = 5000;

export function IncomingCallRecognizedDialog(props) {
  const { call, onAcceptCall, onAcceptCallInSameTab, onRejectCall, callCanceled, onClose } = props;

  const { classes } = useStyles();
  const [showReturnPhoneCallCommunicationCard, setShowReturnPhoneCallCommunicationCard] = useState(false);
  const { organizationContactRolesDict } = useOrganization();

  let claimId;
  let claimIdDisplay;
  let contactId;
  let contactFullName;
  let contactRole;
  let isCallCenterCall = false;

  if (call.customParameters.has('is_call_center_call')) {
    isCallCenterCall = call.customParameters.get('is_call_center_call') === 'true';
  }

  if (call.customParameters.has('claim_id')) {
    // TODO: consider getting the communication instead of the params
    claimId = Number(call.customParameters.get('claim_id'));
    claimIdDisplay = call.customParameters.get('claim_id_display');
  }

  if (call.customParameters.has('contact_id')) {
    contactId = Number(call.customParameters.get('contact_id'));
    contactFullName = call.customParameters.get('contact_full_name');
    contactRole = call.customParameters.get('contact_role');
  }

  let dialogTitle = 'Incoming Phone Call';
  const iconSettings = channelTypeToIconSettings['phone_call'];

  return (
    <>
      <CardDialog title={dialogTitle} maxWidth="xs" fullWidth isDialog>
        <>
          <div className="flex items-center justify-center">
            <div
              className={cn(
                'box-content rounded p-2 text-3xl',
                iconSettings.iconClassName,
                iconSettings.textColor,
                iconSettings.bgColor
              )}
            />
          </div>
          {contactId ? (
            <div className="flex items-center justify-center">
              <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.SEMI_BOLD} className="flex">
                <ContactEntity classes={classes} contactId={contactId} contactDisplayName={contactFullName} />
                <span style={{ marginLeft: '16px' }}>
                  <em>{displayRoleName(organizationContactRolesDict, contactRole)}</em>
                </span>
              </Text>
            </div>
          ) : null}
        </>
        <div className={classes.container} style={{ justifyContent: 'center', alignItems: 'center' }}>
          <Typography display="block" variant="subtitle2">
            {isCallCenterCall ? call.customParameters.get('from_number') : call.parameters.From}
          </Typography>
        </div>
        <br />
        {isCallCenterCall && (
          <div className={classes.container} style={{ justifyContent: 'center', alignItems: 'center' }}>
            <Typography display="block" variant="subtitle2">
              {call.customParameters.get('queue')} Queue Call
            </Typography>
          </div>
        )}
        {claimId && (
          <div className={classes.container} style={{ justifyContent: 'center', alignItems: 'center' }}>
            <ClaimLink claimId={claimId} linkText={claimIdDisplay} />
          </div>
        )}
        <br />
        {callCanceled ? (
          <>
            <Typography display="block" align="center" variant="subtitle2" style={{ color: '#e65100' }}>
              No answer. Call Ended
            </Typography>
            <br />
            <div className={classes.container} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              {!isCallCenterCall && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => setShowReturnPhoneCallCommunicationCard(true)}
                  disabled={call.status() !== 'closed'}
                >
                  <PhoneIcon iconColor="white" className={classes.leftButtonIcon} />
                  Call Back
                </Button>
              )}
              <Button size="small" variant="contained" color="secondary" onClick={() => onClose()}>
                Close
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center space-y-32">
            <>
              <div>
                <Button
                  className="w-[300px]"
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => onAcceptCallInSameTab(call)}
                  disabled={call.status() !== 'pending'}
                >
                  <PhoneIcon iconColor="white" className={classes.leftButtonIcon} />
                  Accept
                </Button>
              </div>
              <Button
                className="w-[300px]"
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => onAcceptCall(call)}
                disabled={call.status() !== 'pending'}
              >
                <PhoneIcon className={classes.leftButtonIcon} />
                Accept And open in a new tab
                <OpenInNewIcon fontSize="small" className={classes.rightButtonIcon} />
              </Button>
              <div className="relative w-full">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-1 border-solid border-slate-600 px-2" />
                </div>
                <div className="relative flex justify-center">
                  <Text className="bg-white px-8" variant="small">
                    Or
                  </Text>
                </div>
              </div>
            </>

            <div>
              <DeclineCallButton
                onDeclineCall={() => onRejectCall(call)}
                disabled={call.status() !== 'pending'}
                isQueueCall={isCallCenterCall}
                shouldDisplay={!callCanceled}
              />
            </div>
          </div>
        )}
      </CardDialog>

      {showReturnPhoneCallCommunicationCard && (
        <NewPhoneCallCommunicationCard
          onCancel={() => setShowReturnPhoneCallCommunicationCard(false)}
          onNewPhoneCallCreated={() => setShowReturnPhoneCallCommunicationCard(false)}
          isNotClaimRelated
          notClaimRelatedCallPhoneNumber={call.parameters.From}
        />
      )}
    </>
  );
}

IncomingCallRecognizedDialog.propTypes = {
  call: PropTypes.object.isRequired,
  callCanceled: PropTypes.bool,
  onAcceptCall: PropTypes.func.isRequired,
  onAcceptCallInSameTab: PropTypes.func.isRequired,
  onRejectCall: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const DeclineCallButton = ({ onDeclineCall, isQueueCall, disabled, shouldDisplay }) => {
  if (!shouldDisplay) {
    return null;
  }

  return (
    <Button
      startIcon={<BlockIcon />}
      className="w-[300px]"
      size="small"
      variant="text"
      color="secondary"
      onClick={onDeclineCall}
      disabled={disabled}
    >
      {isQueueCall ? 'Decline and mark not available' : 'Decline'}
    </Button>
  );
};

DeclineCallButton.propTypes = {
  onDeclineCall: PropTypes.func.isRequired,
  isQueueCall: PropTypes.bool,
  disabled: PropTypes.bool,
  shouldDisplay: PropTypes.bool,
};

function CmsCallCenter() {
  const deviceRef = React.useRef(undefined);
  const [callConnection, setCallConnection] = React.useState(undefined);
  const [callStatus, setCallStatus] = React.useState(undefined);
  const [callCanceled, setCallCanceled] = React.useState(false);

  const { callInProgress, setCallInProgress, userReLogin } = useCms();

  const handleCallAccepted = useCallback((call) => {
    // TODO: Report to backend - should not happen
    setCallStatus(call.status());
  }, []);

  const handleDisconnect = useCallback((call) => {
    // This should not happen
    // TODO: report to backend?
    setCallStatus(call.status());
    setCallConnection(undefined);
  }, []);

  const handleCancel = useCallback(async (parentCallSid) => {
    setCallStatus('closed');
    try {
      const parentCallStatusRes = await axios.get(`/api/v1/calls/call_status/${parentCallSid}`);
      const status = parentCallStatusRes.data.status;
      if (status !== 'in-progress') {
        setCallCanceled(true);
      }
    } catch (error) {
      reportAxiosError(error);
    }
  }, []);

  const handleIncomingCall = useCallback(
    (call) => {
      setCallConnection(call);

      if (!call.customParameters.has('communication_id')) {
        call.ignore();
        // TODO: Report to the backend
        return;
      }

      // Set up call-specific event listeners
      call.on('accept', handleCallAccepted);
      call.on('disconnect', handleDisconnect);
      const parentCallSid = call.customParameters.get('ParentCallSid');
      call.on('cancel', () => handleCancel(parentCallSid));

      setCallStatus(call.status());
      setCallCanceled(false);
    },
    [handleCallAccepted, handleDisconnect, handleCancel]
  );

  async function fetchAccessToken() {
    try {
      const res = await axios.get('/api/v1/calls/twilio_access_token_io');
      const token = res.data;
      return token;
    } catch (error) {
      // Just 'random' 'something went wrong' error won't help the adjuster
      reportAxiosError(error, false);
    }
  }

  function acceptCall(call, answerInSameTab = false) {
    const parentCallSid = call.customParameters.get('ParentCallSid');
    const communicationId = call.customParameters.get('communication_id');
    const claimId = call.customParameters.get('claim_id');
    const currCallSid = call.parameters.CallSid;

    if (answerInSameTab) {
      let newCallInProgress = {
        callSid: parentCallSid,
        currCallSid,
        communicationId,
        direction: 'Incoming',
      };

      if (claimId) {
        newCallInProgress['claimId'] = claimId;
      }

      setCallInProgress(newCallInProgress);
    } else {
      let acceptCallUrl = `/call_in_progress?callSid=${parentCallSid}&currCallSid=${currCallSid}&communicationId=${communicationId}`;

      if (claimId !== undefined) {
        acceptCallUrl += `&claimId=${claimId}`;
      }
      window.open(acceptCallUrl, '_blank');
    }

    call.ignore();
    setCallConnection(undefined);
  }

  function acceptCallInSameTab(call) {
    return acceptCall(call, true);
  }

  function rejectCall(call) {
    call.reject();
    setCallConnection(undefined);
    mixpanel.track(MIXPANEL_EVENTS.INCOMING_PHONE_CALL_COMMUNICATION_DECLINED, {
      communicationId: call.customParameters.get('communication_id'),
    });
    setTimeout(userReLogin, 5000); // Update the status in case it's becoming unavailable
  }

  React.useEffect(() => {
    let isTornDown = false;
    let isReRegistering = false;

    async function handleOffline() {
      if (isReRegistering) {
        // A flapping signaling connection emits 'unregistered' repeatedly. One backed-off loop is
        // the intent; overlapping ones would race several register() calls on the same device.
        return;
      }

      const device = deviceRef.current;
      if (!device) {
        // The teardown clears the ref before destroy(), so the 'unregistered' it emits must not
        // re-register; giving up clears it too, so a dead token endpoint isn't retried forever.
        return;
      }

      // The SDK re-registers by itself once signaling reconnects, usually long before a backoff
      // elapses. register() throws on any state but unregistered, so a device the SDK has
      // registered, or is registering, is left to it.
      const needsReRegister = () => !isTornDown && device.state === Device.State.Unregistered;

      const backOff = (attempt) => new Promise((resolve) => setTimeout(resolve, RE_REGISTER_BACKOFF_MS * attempt));

      isReRegistering = true;
      try {
        let isLastRegisterInterrupted = false;
        // Counted per loop: on a flapping connection the SDK's own re-register fires 'registered'
        // between attempts, and that must not hand this outage a fresh set of attempts.
        for (let attempt = 1; attempt <= MAX_RE_REGISTER_ATTEMPTS; attempt += 1) {
          isLastRegisterInterrupted = false;
          await backOff(attempt);
          if (!needsReRegister()) {
            return;
          }

          const token = await fetchAccessToken();
          if (!needsReRegister()) {
            return;
          }

          if (!token) {
            // fetchAccessToken reports its own failure and resolves undefined. While the device stays
            // unregistered it emits no further 'unregistered', so the next attempt has to come from here.
            continue;
          }

          try {
            device.updateToken(token);
            await device.register();
            return;
          } catch (error) {
            // register() rejects with no reason when the device unregisters again before the
            // registration completes, which leaves nothing to report.
            isLastRegisterInterrupted = !error;
            if (error) {
              reportTwilioError(error);
            }
          }
        }

        // A drop that cuts off a registration leaves the SDK set to register() again once signaling
        // reconnects, so the last attempt's interrupted one gets one more backoff to come back.
        if (isLastRegisterInterrupted && needsReRegister()) {
          await backOff(MAX_RE_REGISTER_ATTEMPTS + 1);
        }

        // The last register() also rejects when destroy() unregisters the device, the teardown's or
        // the SDK's own on pagehide, and a destroyed device did not stay unregistered.
        if (!needsReRegister()) {
          return;
        }

        // Out of attempts: this browser receives no inbound calls until the page is reloaded, and
        // nothing on screen says so. Destroy the device rather than orphaning it past unmount.
        deviceRef.current = undefined;
        device.destroy();
        reportErrorInProductionOrThrow(
          `Twilio device stayed unregistered after ${MAX_RE_REGISTER_ATTEMPTS} re-register attempts - this browser will not receive inbound calls`
        );
      } finally {
        isReRegistering = false;
      }
    }

    const initializeCallCenter = async () => {
      const token = await fetchAccessToken();
      const device = new Device(token, {
        // Disable incoming call sounds since we auto-answer
        sounds: {
          incoming: null,
        },
        tokenRefreshMs: TOKEN_REFRESH_MS,
      });

      attachTokenRefreshHandler(device, fetchAccessToken);
      device.on('registered', () => {
        deviceRef.current = device;
      });
      device.on('unregistered', handleOffline); // offline might happen due to token expiration
      device.on('incoming', handleIncomingCall);
      device.on('error', function (error) {
        if (TWILIO_ERRORS_TO_IGNORE.includes(error.code)) {
          return;
        }

        reportTwilioError(error);
      });

      // Register the device to start receiving events
      await device.register();
    };

    initializeCallCenter();

    return () => {
      isTornDown = true;
      const device = deviceRef.current;
      deviceRef.current = undefined; // We don't want to reinit the device in handleOffline
      if (device) {
        device.destroy();
      }
    };
  }, [handleIncomingCall]);

  if (callInProgress) {
    return <ActiveCallContainer />;
  }

  if (callConnection) {
    if (callStatus === 'pending' || (callStatus === 'closed' && callCanceled)) {
      return (
        <IncomingCallRecognizedDialog
          call={callConnection}
          onAcceptCall={acceptCall}
          onAcceptCallInSameTab={acceptCallInSameTab}
          onRejectCall={rejectCall}
          onClose={() => setCallConnection(undefined)}
          callCanceled={callCanceled}
        />
      );
    }
  }

  return <></>;
}

function ActiveCallContainer() {
  const { callInProgress } = useCms();

  if (!callInProgress) {
    reportErrorInProductionOrThrow();
    return <></>;
  }

  if (callInProgress.direction === 'Incoming') {
    return <IncomingCallInProgressContainer />;
  } else if (callInProgress.direction === 'Outgoing') {
    return <OutgoingCallInProgressContainer />;
  } else {
    reportErrorInProductionOrThrow();
    return <></>;
  }
}

export default CmsCallCenter;
export { TWILIO_ERRORS_TO_IGNORE };
